/**
 * Pure (no Vuex / no DOM) time-zone helpers. Imported by dateHelpers.js,
 * which adds the company-store integration on top.
 *
 * Each function takes the IANA tz id explicitly so tests can run without
 * mocking the store.
 */

export const DEFAULT_TZ = "Asia/Kolkata";

const formatterCache = new Map();

export const isCivilDateString = (s) =>
  typeof s === "string" && /^\d{4}-\d{2}-\d{2}$/.test(s);

export function getFormatter(tz, options) {
  const key = `${tz}|${JSON.stringify(options)}`;
  let f = formatterCache.get(key);
  if (!f) {
    f = new Intl.DateTimeFormat("en-CA", { timeZone: tz, ...options });
    formatterCache.set(key, f);
  }
  return f;
}

export function todayInTz(tz, ref = new Date()) {
  return getFormatter(tz, {
    year: "numeric", month: "2-digit", day: "2-digit",
  }).format(ref);
}

export function daysAgoInTz(n, tz, ref = new Date()) {
  const d = new Date(ref.getTime());
  d.setDate(d.getDate() - n);
  return todayInTz(tz, d);
}

export function nowInTz(tz, ref = new Date()) {
  const parts = getFormatter(tz, {
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false,
  }).formatToParts(ref);
  const get = (t) => parts.find((p) => p.type === t)?.value ?? "";
  const hh = get("hour") === "24" ? "00" : get("hour");
  return `${get("year")}-${get("month")}-${get("day")} ${hh}:${get("minute")}:${get("second")}`;
}

// Formatters used by formatters.js — separated for direct testability.

export function formatDateInTz(dateStr, tz) {
  if (!dateStr) return "";
  if (isCivilDateString(dateStr)) {
    const [y, m, d] = dateStr.split("-").map(Number);
    return new Date(y, m - 1, d).toLocaleDateString("en-IN", {
      day: "2-digit", month: "2-digit", year: "numeric",
    });
  }
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString("en-IN", {
    day: "2-digit", month: "2-digit", year: "numeric", timeZone: tz,
  });
}

export function formatDateLongInTz(dateStr, tz) {
  if (!dateStr) return "";
  if (isCivilDateString(dateStr)) {
    const [y, m, d] = dateStr.split("-").map(Number);
    return new Date(y, m - 1, d).toLocaleDateString("en-IN", {
      day: "2-digit", month: "short", year: "numeric",
    });
  }
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString("en-IN", {
    day: "2-digit", month: "short", year: "numeric", timeZone: tz,
  });
}

export function formatDateTimeInTz(dateStr, tz) {
  if (!dateStr) return "";
  if (isCivilDateString(dateStr)) return formatDateLongInTz(dateStr, tz);
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  return d.toLocaleString("en-IN", {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit", timeZone: tz,
  });
}
